import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, Clock, DollarSign, MapPin, CheckCircle, Compass, PackageCheck, Star, Heart, Share2, ChevronDown, ChevronUp, Percent } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';
import { experiencesData } from '../data/experiencesData';
import PriceDisplay from './PriceDisplay';
import { useCurrency } from '../contexts/CurrencyContext';

// Fix for default marker icons in React-Leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface ExperienceDetailProps {
  experienceId?: number;
}

const ExperienceDetail: React.FC<ExperienceDetailProps> = ({ experienceId: propExperienceId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = propExperienceId || Number(paramId);
  
  const [experience, setExperience] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedFaqs, setExpandedFaqs] = useState<number[]>([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const { formatPrice, currency } = useCurrency();
  
  useEffect(() => {
    // Find the experience by ID
    const foundExperience = experiencesData.find(exp => exp.id === id);
    
    if (foundExperience) {
      // Apply 3% discount to the experience
      const discountedExperience = {
        ...foundExperience,
        originalPrice: foundExperience.price,
        price: Math.round(foundExperience.price * 0.97) // 3% discount
      };
      
      setExperience(discountedExperience);
      // Set document title
      document.title = `${foundExperience.title} | TravelCord`;
    }
    
    setLoading(false);
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [id]);
  
  const toggleFaq = (index: number) => {
    if (expandedFaqs.includes(index)) {
      setExpandedFaqs(expandedFaqs.filter(i => i !== index));
    } else {
      setExpandedFaqs([...expandedFaqs, index]);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ta-green"></div>
      </div>
    );
  }
  
  if (!experience) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Experience Not Found</h1>
        <p className="text-gray-600 mb-6">The experience you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className="px-4 py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors">
          Return to Homepage
        </Link>
      </div>
    );
  }
  
  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black/60 z-10"></div>
          <img 
            src={experience.image} 
            alt={experience.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Experiences
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-2">
              <span className="bg-ta-green text-white text-xs px-2 py-1 rounded-full">
                {experience.category}
              </span>
              <div className="flex items-center ml-3 text-white/90">
                <Star className="h-4 w-4 fill-current text-yellow-400 mr-1" />
                <span>{experience.rating}</span>
                <span className="mx-1">•</span>
                <span>{experience.reviews} reviews</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{experience.title}</h1>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center">
                <MapPin className="h-5 w-5 mr-1 text-ta-green" />
                <span>{experience.location}</span>
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-1 text-ta-green" />
                <span>{experience.duration}</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-1 text-ta-green" />
                <span>{experience.groupSize}</span>
              </div>
              <div className="flex items-center">
                <DollarSign className="h-5 w-5 mr-1 text-ta-green" />
                {experience.originalPrice ? (
                  <span className="flex items-center">
                    From <span className="line-through mx-1">{formatPrice(experience.originalPrice)}</span> 
                    <span className="font-bold">{formatPrice(experience.price)}</span>
                    <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-sm flex items-center">
                      <Percent className="h-3 w-3 mr-0.5" />
                      -3%
                    </span>
                  </span>
                ) : (
                  <span>From {formatPrice(experience.price)} per person</span>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => setIsBookingModalOpen(true)}
                className="px-6 py-3 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors flex items-center"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Book Now
              </button>
              <button 
                onClick={() => setIsQuoteModalOpen(true)}
                className="px-6 py-3 border border-white text-white rounded-md hover:bg-white/10 transition-colors flex items-center"
              >
                Request Quote
              </button>
              <button className="p-3 border border-white/30 text-white rounded-md hover:bg-white/10 transition-colors">
                <Heart className="h-5 w-5" />
              </button>
              <button className="p-3 border border-white/30 text-white rounded-md hover:bg-white/10 transition-colors">
                <Share2 className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Navigation Tabs */}
      <section className="sticky top-14 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-4 font-medium text-sm whitespace-nowrap border-b-2 ${activeTab === 'overview' ? 'border-ta-green text-ta-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('itinerary')}
              className={`px-4 py-4 font-medium text-sm whitespace-nowrap border-b-2 ${activeTab === 'itinerary' ? 'border-ta-green text-ta-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Itinerary
            </button>
            <button 
              onClick={() => setActiveTab('packing')}
              className={`px-4 py-4 font-medium text-sm whitespace-nowrap border-b-2 ${activeTab === 'packing' ? 'border-ta-green text-ta-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              What to Pack
            </button>
            <button 
              onClick={() => setActiveTab('locations')}
              className={`px-4 py-4 font-medium text-sm whitespace-nowrap border-b-2 ${activeTab === 'locations' ? 'border-ta-green text-ta-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              Locations
            </button>
            <button 
              onClick={() => setActiveTab('faqs')}
              className={`px-4 py-4 font-medium text-sm whitespace-nowrap border-b-2 ${activeTab === 'faqs' ? 'border-ta-green text-ta-green' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              FAQs
            </button>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Experience Overview</h2>
                  <p className="text-gray-700 mb-6">{experience.description}</p>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Highlights</h3>
                  <ul className="space-y-2 mb-6">
                    {experience.highlights.map((highlight: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">What's Included</h3>
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Professional guide</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Park entrance fees</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Activity permits</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Bottled water</span>
                      </li>
                    </ul>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">What's Not Included</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <ChevronDown className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Gratuities</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronDown className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Personal expenses</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronDown className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Travel insurance</span>
                      </li>
                      <li className="flex items-start">
                        <ChevronDown className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Accommodation (unless specified)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Itinerary Tab */}
              {activeTab === 'itinerary' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Detailed Itinerary</h2>
                  <p className="text-gray-700 mb-6">
                    Follow this journey through {experience.location} as you experience {experience.title}.
                  </p>
                  
                  <div className="space-y-6 mb-6">
                    {experience.itinerary.map((item: any, index: number) => (
                      <div key={index} className="relative pl-8 pb-6 border-l-2 border-ta-green last:border-l-0 last:pb-0">
                        <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-ta-green"></div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-700">{item.description}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Important Notes</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Itinerary may be adjusted based on weather conditions and wildlife movements</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Please arrive at least 15 minutes before the scheduled start time</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">This experience requires a moderate level of fitness</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Packing Tab */}
              {activeTab === 'packing' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">What to Pack</h2>
                  <p className="text-gray-700 mb-6">
                    Be prepared for your {experience.title} adventure with these essential items.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {experience.packingList.map((item: string, index: number) => (
                      <div key={index} className="flex items-start">
                        <PackageCheck className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Packing Tips</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Pack light but be prepared for changing weather conditions</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Neutral colors (khaki, olive, beige) are best for wildlife viewing</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Bring a small daypack for carrying essentials during activities</span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Locations Tab */}
              {activeTab === 'locations' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Locations</h2>
                  <p className="text-gray-700 mb-6">
                    Explore the stunning locations you'll visit during your {experience.title} experience.
                  </p>
                  
                  <div className="space-y-6 mb-6">
                    {experience.locations.map((location: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-6 rounded-lg">
                        <div className="flex items-start mb-3">
                          <MapPin className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-1" />
                          <h3 className="text-xl font-semibold text-gray-900">{location.name}</h3>
                        </div>
                        <p className="text-gray-700 mb-4">{location.description}</p>
                        <div className="h-48 rounded-lg overflow-hidden">
                          <MapContainer 
                            center={[location.coordinates.lat, location.coordinates.lng]} 
                            zoom={10} 
                            style={{ height: '100%', width: '100%' }}
                          >
                            <TileLayer
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <Marker position={[location.coordinates.lat, location.coordinates.lng]}>
                              <Popup>
                                <div className="p-2">
                                  <h3 className="font-semibold text-gray-900">{location.name}</h3>
                                  <p className="text-sm text-gray-600">{location.description}</p>
                                </div>
                              </Popup>
                            </Marker>
                          </MapContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* FAQs Tab */}
              {activeTab === 'faqs' && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
                  <p className="text-gray-700 mb-6">
                    Find answers to common questions about the {experience.title} experience.
                  </p>
                  
                  <div className="space-y-4 mb-6">
                    {experience.faqs.map((faq: any, index: number) => (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleFaq(index)}
                          className="w-full flex items-center justify-between p-4 text-left focus:outline-none"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          {expandedFaqs.includes(index) ? (
                            <ChevronUp className="h-5 w-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500" />
                          )}
                        </button>
                        
                        {expandedFaqs.includes(index) && (
                          <div className="p-4 pt-0 text-gray-700 border-t border-gray-200">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Have More Questions?</h3>
                    <p className="text-gray-700 mb-4">
                      If you can't find the answer to your question, please feel free to contact us directly.
                    </p>
                    <button 
                      onClick={() => setIsQuoteModalOpen(true)}
                      className="px-4 py-2 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors"
                    >
                      Contact Us
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Book This Experience</h3>
                  <div className="flex items-center mb-4">
                    {experience.originalPrice ? (
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-gray-900">{formatPrice(experience.price)}</span>
                        <span className="ml-2 text-sm text-gray-500 line-through">{formatPrice(experience.originalPrice)}</span>
                        <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-sm flex items-center">
                          <Percent className="h-3 w-3 mr-0.5" />
                          -3%
                        </span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-gray-900">{formatPrice(experience.price)}</span>
                    )}
                    <span className="text-gray-600 ml-1">per person</span>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Duration</h4>
                        <p className="text-gray-600">{experience.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <Users className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Group Size</h4>
                        <p className="text-gray-600">{experience.groupSize}</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900">Location</h4>
                        <p className="text-gray-600">{experience.location}</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="w-full py-3 bg-ta-green text-white rounded-md hover:bg-ta-green-dark transition-colors mb-3"
                  >
                    Book Now
                  </button>
                  
                  <button
                    onClick={() => setIsQuoteModalOpen(true)}
                    className="w-full py-3 border border-ta-green text-ta-green rounded-md hover:bg-ta-green/5 transition-colors"
                  >
                    Request a Quote
                  </button>
                </div>
                
                <div className="bg-gray-50 p-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">Why Book With Us</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Free cancellation up to 24 hours before</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Verified local guides</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-ta-green mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Best price guarantee</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Experiences */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Experiences You Might Like</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {experiencesData
              .filter(exp => exp.id !== experience.id && exp.category === experience.category)
              .slice(0, 3)
              .map(relatedExp => {
                // Apply 3% discount to related experiences
                const discountedPrice = Math.round(relatedExp.price * 0.97);
                
                return (
                  <Link 
                    key={relatedExp.id} 
                    to={`/experiences/${relatedExp.id}`}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300"
                  >
                    <div className="relative h-48">
                      <img 
                        src={relatedExp.image} 
                        alt={relatedExp.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-2 left-2 bg-ta-green text-white text-xs px-2 py-0.5 rounded-sm">
                        {relatedExp.category}
                      </div>
                      {/* Discount Tag */}
                      <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-sm flex items-center">
                        <Percent className="h-3 w-3 mr-0.5" />
                        -3%
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center mb-1">
                        <div className="flex items-center text-ta-green">
                          <Star className="h-3 w-3 fill-current" />
                          <span className="ml-1 text-xs font-medium">{relatedExp.rating}</span>
                        </div>
                        <span className="mx-1 text-gray-400 text-xs">•</span>
                        <span className="text-gray-600 text-xs">{relatedExp.reviews} reviews</span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">{relatedExp.title}</h3>
                      <div className="flex items-center text-gray-600 mb-1">
                        <MapPin className="h-3 w-3 text-ta-green-dark" />
                        <span className="ml-1 text-xs">{relatedExp.location}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <div>
                          <p className="text-[10px] text-gray-500">from</p>
                          <div className="flex items-center">
                            <p className="text-sm font-bold text-gray-900">{formatPrice(discountedPrice)}</p>
                            <p className="text-xs text-gray-500 line-through ml-1">{formatPrice(relatedExp.price)}</p>
                          </div>
                        </div>
                        <span className="py-1 px-2 text-xs bg-ta-green text-white rounded-md">
                          View Details
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>
      
      {/* Booking Modal */}
      <BookingModal 
        experience={experience}
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
      />
      
      {/* Quote Request Modal */}
      <BookingModal 
        experience={experience}
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
      />
      
      <Footer />
    </>
  );
};

export default ExperienceDetail;